import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  catchError,
  map,
  Observable,
  of,
  OperatorFunction,
  pipe,
  retry,
  switchMap,
  tap
} from 'rxjs';
import { ApiSchema } from './http-api-schema';
import { ApiException } from '@helpers/errors/api-error';
import { HttpTranslatorDecorator } from './http-translate-decorator';
import { APP_CONFIG } from '@cfg/env/app.config';

/**
 * Servicio para realizar simplificar la ejecución de solicitudes HTTP hacia una API
 */
@Injectable({
  providedIn: 'root'
})
export class AppHttpClientApi {
  /**
   * Configuración de la aplicación
   */
  private readonly appConfig = inject(APP_CONFIG);

  /**
   * Servicio HttpClient de angular
   */
  private readonly httpClient = inject(HttpClient);

  /**
   * Decorador para traducir los mensajes de respuesta de la API
   */
  private readonly translateDecorator = inject(HttpTranslatorDecorator);

  /**
   * Opciones comunes para las solicitudes HTTP
   */
  private readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  /**
   * Configuración por defecto para reintentos de solicitudes HTTP
   */
  private readonly defaultRetries = { count: 2, delay: 200 };

  /**
   * Construye la URL base según la configuración
   * Si baseUrl está definido, lo usa. Si no, solo usa el context (para proxy local)
   */
  private buildBaseUrl(): string {
    const baseUrl = this.appConfig.backend.baseUrl || '';
    return `${baseUrl}${this.appConfig.backend.context}`;
  }

  /**
   * Realiza una solicitud HTTP de tipo GET
   * @param url URL de la solicitud
   */
  get<T>(url: string): Observable<T | undefined> {
    return this.httpClient
      .get<ApiSchema<T>>(`${this.buildBaseUrl()}/${url}`, this.httpOptions)
      .pipe(
        retry(this.defaultRetries),
        this.handleError<T>(),
        this.handleResponse<T>()
      );
  }

  /**
   * Realiza una solicitud HTTP de tipo POST
   * @param url URL de la solicitud
   * @param body Cuerpo de la solicitud
   */
  post<T>(
    url: string,
    body: unknown,
    customHeaders?: HttpHeaders
  ): Observable<T | undefined> {
    const opts = {
      headers: this.httpOptions.headers
    };
    if (customHeaders) {
      opts.headers = customHeaders;
    }

    return this.httpClient
      .post<ApiSchema<T>>(`${this.buildBaseUrl()}/${url}`, body, opts)
      .pipe(
        retry(this.defaultRetries),
        this.handleError<T>(),
        this.handleResponse<T>()
      );
  }

  /**
   * Realiza una solicitud HTTP de tipo PUT
   * @param url URL de la solicitud
   * @param body Cuerpo de la solicitud
   */
  put<T>(url: string, body: unknown): Observable<T | undefined> {
    return this.httpClient
      .put<
        ApiSchema<T>
      >(`${this.buildBaseUrl()}/${url}`, body, this.httpOptions)
      .pipe(
        retry(this.defaultRetries),
        this.handleError<T>(),
        this.handleResponse<T>()
      );
  }

  patch<T>(url: string, body: unknown): Observable<T | undefined> {
    return this.httpClient
      .patch<
        ApiSchema<T>
      >(`${this.buildBaseUrl()}/${url}`, body, this.httpOptions)
      .pipe(
        retry(this.defaultRetries),
        this.handleError<T>(),
        this.handleResponse<T>()
      );
  }

  delete(url: string): Observable<void> {
    return this.httpClient
      .delete(`${this.buildBaseUrl()}/${url}`, this.httpOptions)
      .pipe(
        retry(this.defaultRetries),
        map(() => undefined),
        catchError((err: unknown) => {
          if (err instanceof HttpErrorResponse) {
            const schema = err.error as ApiSchema<unknown>;
            return of(schema);
          }

          throw err;
        }),
        switchMap((response) => {
          if (response) {
            return this.translateDecorator.translate(response);
          }
          return of(undefined);
        }),
        switchMap((response) => {
          if (response) {
            throw new ApiException(response.errors!);
          }
          return of(undefined);
        })
      );
  }

  /**
   * Raw GET request without ApiSchema wrapper
   * For endpoints that return direct data structures (pagination, blobs, etc)
   * @param url URL relative to backend context
   * @param options Optional HttpClient options (params, headers, etc)
   */
  getRaw<T>(
    url: string,
    options?: {
      params?: HttpParams;
      headers?: HttpHeaders;
      responseType?: 'json';
    }
  ): Observable<T> {
    const fullUrl = `${this.buildBaseUrl()}/${url}`;
    return this.httpClient
      .get<T>(fullUrl, {
        ...this.httpOptions,
        ...options
      })
      .pipe(retry(this.defaultRetries));
  }

  postRaw<T>(
    url: string,
    body: unknown,
    options?: { headers?: HttpHeaders; context?: HttpContext }
  ): Observable<T> {
    const opts: { headers: HttpHeaders; context?: HttpContext } = {
      headers: options?.headers ?? this.httpOptions.headers
    };
    if (options?.context) {
      opts.context = options.context;
    }

    const fullUrl = `${this.buildBaseUrl()}/${url}`;
    return this.httpClient
      .post<T>(fullUrl, body, opts)
      .pipe(retry(this.defaultRetries));
  }

  patchRaw<T>(
    url: string,
    body: unknown,
    options?: { context?: HttpContext }
  ): Observable<T> {
    const fullUrl = `${this.buildBaseUrl()}/${url}`;
    const opts: { headers: HttpHeaders; context?: HttpContext } = {
      ...this.httpOptions
    };
    if (options?.context) {
      opts.context = options.context;
    }
    return this.httpClient
      .patch<T>(fullUrl, body, opts)
      .pipe(retry(this.defaultRetries));
  }

  /**
   * Raw GET request for Blob responses (file downloads)
   * @param url URL relative to backend context
   * @param options Optional HttpClient options
   */
  getBlob(
    url: string,
    options?: {
      params?: HttpParams;
      headers?: HttpHeaders;
    }
  ): Observable<Blob> {
    const fullUrl = `${this.buildBaseUrl()}/${url}`;
    return this.httpClient
      .get(fullUrl, {
        ...options,
        responseType: 'blob'
      })
      .pipe(retry(this.defaultRetries));
  }

  private handleError<T>(): OperatorFunction<ApiSchema<T>, ApiSchema<T>> {
    return catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        const schema = err.error as ApiSchema<T>;
        return this.translateDecorator.translate(schema).pipe(
          map((response) => {
            if (response.errors && response.errors.length > 0) {
              throw new ApiException(response.errors);
            }
            return response;
          })
        );
      }

      throw err;
    });
  }

  private handleResponse<T>() {
    return pipe(
      tap((response: ApiSchema<T>) => {
        console.log('[handleResponse] RAW backend response:', response);
      }),
      switchMap((response: ApiSchema<T>) =>
        this.translateDecorator.translate(response)
      ),
      tap((response: ApiSchema<T>) => {
        console.log('[handleResponse] After translate:', response);
      }),
      map((response: ApiSchema<T>) => {
        console.log('[handleResponse] Extracting results:', response.results);
        return response.results;
      })
    );
  }
}
